import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ArrowUpRight, TrendingUp, Building2, Coins, Rocket } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, CompanyAvatar } from "@/components/StatusBadge";
import {
  BOLAG,
  FASER,
  formatSEK,
  totalKapital,
  offentligaTyper,
  type Bransch,
} from "@/lib/portfolio-data";

export const Route = createFileRoute("/oversikt")({
  head: () => ({
    meta: [
      { title: "Översikt — Inkubera Portfolio Intelligence" },
      {
        name: "description",
        content:
          "Executive dashboard för Inkuberas bolagsportfölj — pipeline, mognadsgrad och finansiering i realtid.",
      },
    ],
  }),
  component: Dashboard,
});

function Dashboard() {
  const aktiva = BOLAG.filter((b) => !["Exit", "Alumni", "Pausat"].includes(b.fas));
  const exits = BOLAG.filter((b) => b.fas === "Exit").length;
  const allFin = BOLAG.flatMap((b) => b.finansiering);
  const totaltKapital = allFin.reduce((s, f) => s + f.belopp, 0);
  const offentligt = allFin
    .filter((f) => offentligaTyper.includes(f.typ))
    .reduce((s, f) => s + f.belopp, 0);
  const nya12mn = BOLAG.filter(
    (b) => new Date(b.startdatum) > new Date(Date.now() - 365 * 24 * 3600 * 1000),
  ).length;

  // Kapital över tid
  const byYear: Record<string, { år: string; Privat: number; Offentligt: number }> = {};
  for (let y = 2019; y <= 2025; y++) byYear[y] = { år: String(y), Privat: 0, Offentligt: 0 };
  allFin.forEach((f) => {
    const y = new Date(f.datum).getFullYear();
    if (!byYear[y]) return;
    if (offentligaTyper.includes(f.typ)) byYear[y].Offentligt += f.belopp / 1_000_000;
    else byYear[y].Privat += f.belopp / 1_000_000;
  });
  const kapitalData = Object.values(byYear);

  // Bransch-fördelning
  const branschCount: Record<string, number> = {};
  BOLAG.forEach((b) => b.branscher.forEach((x) => (branschCount[x] = (branschCount[x] || 0) + 1)));
  const branschData = Object.entries(branschCount)
    .map(([n, v]) => ({ name: n, value: v }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  // Pipeline counts
  const pipeline = FASER.map((f) => ({ fas: f, n: BOLAG.filter((b) => b.fas === f).length }));

  // Senaste händelser
  const händelser = BOLAG.flatMap((b) =>
    b.milstolpar.map((m) => ({ ...m, bolag: b })),
  )
    .sort((a, b) => +new Date(b.datum) - +new Date(a.datum))
    .slice(0, 5);

  const palette = [
    "var(--chart-1)",
    "var(--chart-2)",
    "var(--chart-3)",
    "var(--chart-4)",
    "var(--chart-5)",
    "var(--accent)",
    "var(--info)",
    "var(--warning)",
  ];

  return (
    <AppShell>
      {/* Hero */}
      <section className="relative px-4 sm:px-8 pt-6 sm:pt-10 pb-6 sm:pb-8 border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-6xl">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Översikt · {new Date().toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-gradient max-w-3xl">
            Inkubera i Örebro — spetsinkubator inom AI, Life Science och Deeptech.
          </h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-muted-foreground max-w-xl">
            {aktiva.length} aktiva bolag och innovationer i portföljen, {exits} på väg mot exit.
          </p>
        </div>
      </section>

      {/* KPI */}
      <section className="px-4 sm:px-8 py-6 sm:py-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi
          label="Aktiva bolag"
          value={aktiva.length}
          icon={Building2}
          delta={`+${nya12mn} senaste året`}
        />
        <Kpi
          label="Totalt kapital"
          value={formatSEK(totaltKapital)}
          icon={Coins}
          delta={`${formatSEK(offentligt)} offentligt`}
        />
        <Kpi
          label="Exits"
          value={exits}
          icon={Rocket}
          delta="Klarna, Atomico m.fl."
        />
        <Kpi
          label="Portföljhälsa"
          value={`${Math.round(BOLAG.reduce((s, b) => s + b.hälsoscore, 0) / BOLAG.length)}`}
          unit="/ 100"
          icon={TrendingUp}
          delta="Stabil utveckling"
        />
      </section>

      {/* Charts grid */}
      <section className="px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader title="Kapital över tid" subtitle="Privat vs offentligt, miljoner SEK" />
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={kapitalData} margin={{ left: -10, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gPrivat" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOff" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-2)" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="var(--chart-2)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="år" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} unit=" Mkr" />
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="Privat" stroke="var(--chart-1)" fill="url(#gPrivat)" strokeWidth={2} />
                <Area type="monotone" dataKey="Offentligt" stroke="var(--chart-2)" fill="url(#gOff)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <CardHeader title="Branscher" subtitle="Fördelning i portföljen" />
          <div className="h-72 flex items-center">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={branschData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={86} paddingAngle={2}>
                  {branschData.map((_, i) => (
                    <Cell key={i} fill={palette[i % palette.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="px-5 pb-5 grid grid-cols-2 gap-1.5 text-xs">
            {branschData.slice(0, 6).map((b, i) => (
              <div key={b.name} className="flex items-center gap-2 text-muted-foreground">
                <span className="size-2 rounded-sm" style={{ background: palette[i % palette.length] }} />
                <span className="truncate">{b.name}</span>
                <span className="ml-auto text-foreground">{b.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </section>

      <section className="px-4 sm:px-8 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader title="Pipeline" subtitle="Bolag per fas" actionHref="/pipeline" actionLabel="Öppna pipeline" />
          <div className="h-60">
            <ResponsiveContainer>
              <BarChart data={pipeline} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="fas" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "color-mix(in oklab, var(--foreground) 4%, transparent)" }}
                  contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }}
                />
                <Bar dataKey="n" fill="var(--foreground)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <CardHeader title="Senaste händelser" subtitle="Milstolpar i portföljen" />
          <ul className="px-5 pb-5 space-y-3">
            {händelser.map((h, i) => (
              <li key={i} className="flex gap-3">
                <CompanyAvatar initialer={h.bolag.initialer} farg={h.bolag.logoFarg} size={32} />
                <div className="min-w-0 flex-1">
                  <Link
                    to="/bolag/$id"
                    params={{ id: h.bolag.id }}
                    className="text-sm font-medium hover:underline truncate block"
                  >
                    {h.rubrik}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {h.bolag.namn} · {new Date(h.datum).toLocaleDateString("sv-SE")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </section>

      {/* Top bolag */}
      <section className="px-4 sm:px-8 py-6 sm:py-8">
        <div className="flex items-end justify-between mb-4 gap-3">
          <h2 className="font-display text-xl sm:text-2xl">Bolag att hålla koll på</h2>
          <Link to="/portfolj" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            Hela portföljen <ArrowUpRight className="size-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...BOLAG]
            .sort((a, b) => totalKapital(b) - totalKapital(a))
            .slice(0, 6)
            .map((b) => (
              <Link
                key={b.id}
                to="/bolag/$id"
                params={{ id: b.id }}
                className="group p-5 rounded-2xl border border-border bg-card hover:border-foreground/40 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-3">
                  <CompanyAvatar initialer={b.initialer} farg={b.logoFarg} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-lg leading-tight truncate">{b.namn}</h3>
                      <ArrowUpRight className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{b.ort}</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{b.beskrivning}</p>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <StatusBadge fas={b.fas} />
                  {b.branscher.slice(0, 2).map((br: Bransch) => (
                    <span
                      key={br}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground"
                    >
                      {br}
                    </span>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Kapital</div>
                    <div className="font-display text-base">{formatSEK(totalKapital(b))}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Hälsa</div>
                    <div className="font-display text-base">{b.hälsoscore}</div>
                  </div>
                </div>
              </Link>
            ))}
        </div>
      </section>
    </AppShell>
  );
}

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-border bg-card ${className}`}>{children}</div>;
}

function CardHeader({
  title,
  subtitle,
  actionHref,
  actionLabel,
}: {
  title: string;
  subtitle?: string;
  actionHref?: string;
  actionLabel?: string;
}) {
  return (
    <div className="flex items-start justify-between p-5 pb-3">
      <div>
        <h3 className="font-display text-lg">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {actionHref && (
        <Link to={actionHref} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          {actionLabel} <ArrowUpRight className="size-3" />
        </Link>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  unit,
  icon: Icon,
  delta,
}: {
  label: string;
  value: string | number;
  unit?: string;
  icon: React.ComponentType<{ className?: string }>;
  delta?: string;
}) {
  return (
    <div className="p-4 sm:p-5 rounded-2xl bg-card border border-border relative overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground truncate">{label}</span>
        <div className="size-8 rounded-lg bg-secondary flex items-center justify-center shrink-0">
          <Icon className="size-4 text-foreground" />
        </div>
      </div>
      <div className="mt-3 sm:mt-4 flex items-baseline gap-1 flex-wrap">
        <div className="font-display text-2xl sm:text-4xl">{value}</div>
        {unit && <div className="text-sm text-muted-foreground">{unit}</div>}
      </div>
      {delta && <div className="mt-1 text-[11px] sm:text-xs text-muted-foreground">{delta}</div>}
    </div>
  );
}
